import type { QueryClient } from "@tanstack/react-query";
import {
  BADGE_RULES_STALE_MS,
  badgeRulesQueryKey,
  fetchBadgeRules,
} from "@/lib/badgeRules";
import {
  discoveryFeedQueryKey,
  loadMappedDiscoveryFeed,
} from "@/lib/discoveryFeedMap";
import {
  fetchMapVenues,
  mapVenuesQueryKey,
  type OpenVenue,
  type PulseEvent,
} from "@/lib/events";
import { getVenueOpenStatus } from "@/lib/venueHours";
import { mediaUrl } from "@/lib/storageImageUrl";
import {
  COMPACT_VENUE_CARD_HEIGHT,
  COMPACT_VENUE_CARD_WIDTH,
} from "@/lib/venueCardStyles";

const MAP_VENUES_PREFETCH_STALE_MS = 1000 * 60 * 30;
const DISCOVERY_FEED_STALE_MS = 60_000;

/** Match mobile DISCOVERY_SPLASH_CAP_MS — hide splash even if work unfinished. */
export const DISCOVERY_SPLASH_CAP_MS = 3000;

/** Card preset src width/aspect — same transform EventImage uses for Discovery cards. */
const CARD_TRANSFORM = {
  width: 400,
  height: Math.round(400 / (COMPACT_VENUE_CARD_WIDTH / COMPACT_VENUE_CARD_HEIGHT)),
  resize: "cover" as const,
  quality: 75,
};

export type SplashPrefetchPhase = "idle" | "feed" | "images" | "done" | "capped";

export type SplashPrefetchResult = {
  capped: boolean;
  disabled: boolean;
  feedMs: number | null;
  imagesMs: number | null;
  totalMs: number;
  imageCount: number;
};

type ProgressListener = (phase: SplashPrefetchPhase) => void;

const listeners = new Set<ProgressListener>();
let phase: SplashPrefetchPhase = "idle";
let bootstrapPromise: Promise<SplashPrefetchResult> | null = null;

function now(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function setPhase(next: SplashPrefetchPhase): void {
  phase = next;
  for (const listener of listeners) listener(next);
}

export function getSplashPrefetchPhase(): SplashPrefetchPhase {
  return phase;
}

export function subscribeSplashPrefetchPhase(listener: ProgressListener): () => void {
  listeners.add(listener);
  listener(phase);
  return () => {
    listeners.delete(listener);
  };
}

export function isDiscoverySplashPrefetchDisabled(): boolean {
  return import.meta.env.VITE_DISABLE_DISCOVERY_PREFETCH === "1";
}

function stallMs(): number {
  const raw = import.meta.env.VITE_SPLASH_PREFETCH_STALL_MS;
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function rankFeatured(venue: OpenVenue): number {
  const open =
    getVenueOpenStatus(venue.opening_hours, venue.category, new Date(), venue.name) === "open"
      ? 0
      : 1;
  if (venue.isTopPick) return 0 + open;
  if (venue.isPopular) return 2 + open;
  return 4 + open;
}

/**
 * First visible Discovery images: Highlights + first A–Z cards + live/current events.
 * Caps at `limit` unique card-sized transform URLs.
 */
export function collectInitialDiscoveryImageUrls(
  venues: OpenVenue[],
  events: PulseEvent[],
  limit = 15,
): string[] {
  const highlights = [...venues]
    .sort((a, b) => rankFeatured(a) - rankFeatured(b) || a.distanceKm - b.distanceKm)
    .slice(0, 8);
  const alphaFirst = [...venues]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    .slice(0, 6);
  const live = events.filter((e) => e.live).slice(0, 4);
  const eventFallback = live.length > 0 ? live : events.slice(0, 4);

  const urls: string[] = [];
  const seen = new Set<string>();

  const push = (raw: string | null | undefined) => {
    const trimmed = raw?.trim();
    if (!trimmed) return;
    const url = mediaUrl(trimmed, CARD_TRANSFORM);
    if (seen.has(url)) return;
    seen.add(url);
    urls.push(url);
  };

  for (const venue of highlights) push(venue.image);
  for (const venue of alphaFirst) push(venue.image);
  for (const event of eventFallback) push(event.image);

  return urls.slice(0, limit);
}

function prefetchDiscoveryImageUrl(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof Image === "undefined") {
      void fetch(url)
        .then((res) => resolve(res.ok))
        .catch(() => resolve(false));
      return;
    }
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

export async function prefetchDiscoveryImageUrls(urls: string[]): Promise<number> {
  if (urls.length === 0) return 0;
  const results = await Promise.allSettled(urls.map((url) => prefetchDiscoveryImageUrl(url)));
  return results.filter((r) => r.status === "fulfilled" && r.value === true).length;
}

async function runPrefetchWork(
  queryClient: QueryClient,
  city: string,
): Promise<Omit<SplashPrefetchResult, "capped" | "totalMs">> {
  if (isDiscoverySplashPrefetchDisabled()) {
    setPhase("done");
    return { disabled: true, feedMs: null, imagesMs: null, imageCount: 0 };
  }

  const artificialStall = stallMs();
  if (artificialStall > 0) {
    await sleep(artificialStall);
  }

  setPhase("feed");
  const feedStarted = now();

  // Phase 4: same get_discovery_feed path as Home/Map (no dual client staffelung).
  void queryClient.prefetchQuery({
    queryKey: mapVenuesQueryKey(city),
    queryFn: () => fetchMapVenues(city),
    staleTime: MAP_VENUES_PREFETCH_STALE_MS,
  });
  void queryClient.prefetchQuery({
    queryKey: badgeRulesQueryKey,
    queryFn: fetchBadgeRules,
    staleTime: BADGE_RULES_STALE_MS,
  });

  let venues: OpenVenue[] = [];
  let events: PulseEvent[] = [];
  try {
    const feed = await queryClient.fetchQuery({
      queryKey: discoveryFeedQueryKey(city),
      queryFn: () => loadMappedDiscoveryFeed(city),
      staleTime: DISCOVERY_FEED_STALE_MS,
    });
    venues = feed.venues;
    events = feed.events;
  } catch {
    // Prefetch is best-effort; HomePage will fetch on mount.
  }
  const feedMs = Math.round(now() - feedStarted);

  setPhase("images");
  const imagesStarted = now();
  const urls = collectInitialDiscoveryImageUrls(venues, events, 15);
  const imageCount = await prefetchDiscoveryImageUrls(urls);
  const imagesMs = Math.round(now() - imagesStarted);

  setPhase("done");
  return { disabled: false, feedMs, imagesMs, imageCount };
}

/**
 * Starts Discovery feed + first card-image prefetch once.
 * Resolves when work finishes OR after DISCOVERY_SPLASH_CAP_MS — whichever first.
 * Does not artificially extend past work completion.
 */
export function startDiscoverySplashPrefetch(
  queryClient: QueryClient,
  city: string,
): Promise<SplashPrefetchResult> {
  if (bootstrapPromise) return bootstrapPromise;

  setPhase("idle");
  const started = now();

  bootstrapPromise = (async () => {
    let settled: Omit<SplashPrefetchResult, "capped" | "totalMs"> | null = null;
    let capped = false;

    const work = runPrefetchWork(queryClient, city)
      .then((result) => {
        settled = result;
        return result;
      })
      .catch(() => {
        setPhase("done");
        settled = {
          disabled: false,
          feedMs: null,
          imagesMs: null,
          imageCount: 0,
        };
        return settled;
      });

    const winner = await Promise.race([
      work.then(() => "done" as const),
      sleep(DISCOVERY_SPLASH_CAP_MS).then(() => "cap" as const),
    ]);

    if (winner === "cap" && !settled) {
      capped = true;
      setPhase("capped");
    }

    const base = settled ?? {
      disabled: isDiscoverySplashPrefetchDisabled(),
      feedMs: null,
      imagesMs: null,
      imageCount: 0,
    };

    return {
      ...base,
      capped,
      totalMs: Math.round(now() - started),
    };
  })();

  return bootstrapPromise;
}

/** Promise started by router bootstrap; resolves immediately if never started. */
export function getDiscoverySplashPrefetchPromise(): Promise<SplashPrefetchResult> {
  return (
    bootstrapPromise ??
    Promise.resolve({
      capped: false,
      disabled: true,
      feedMs: null,
      imagesMs: null,
      totalMs: 0,
      imageCount: 0,
    })
  );
}

/** Test helper — clears singleton between measurement runs. */
export function resetDiscoverySplashPrefetchForTests(): void {
  bootstrapPromise = null;
  phase = "idle";
}
